import React, { useCallback } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { useEnsureContext } from 'common/hooks';
import { useParams } from 'react-router';
import { message, Skeleton } from 'antd';
import { BusinessPartnerPatientEditForm } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/shared/BusinessPartnerPatientEditForm';
import { FormikOnSubmit } from 'lib/formik.utils';
import { BusinessPartnerPatientFormModel } from 'models/patient';
import { SaveButton } from 'common/components/SaveButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export const BusinessPartnerPatientPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { patientId } = useParams();
  const queryCache = useQueryCache();

  const { isLoading, data } = useQuery(
    [QueryKey.BusinessPartnerPatient, bpContext.id, patientId],
    () => BusinessPartnersAPI.fetchPatientById(bpContext.id, patientId)
  );

  const [mutate, mutationHelpers] = useMutation(
    BusinessPartnersAPI.modifyPatientById
  );

  const removeSpacesFromForm = (formData: BusinessPartnerPatientFormModel) => {
    formData.phoneNumber = formData.phoneNumber
      .replace(/\s/g, '')
      .replace(/^0+/, '');
    formData.prefixCode = formData.prefixCode.replace(/\s/g, '');
    return formData;
  };

  const intlMessage = useFormatMessage();

  const handleSubmit: FormikOnSubmit<BusinessPartnerPatientFormModel> = useCallback(
    (formData) => {
      const formDataToSend =
        formData.phoneNumber !== null
          ? removeSpacesFromForm(formData)
          : formData;

      mutate(
        { businessPartnerId: bpContext.id, dto: formDataToSend },
        {
          onSuccess: () => {
            queryCache
              .invalidateQueries(QueryKey.BusinessPartnerPatient)
              .then();
            message.success(
              intlMessage(
                { id: 'entity.updated' },
                {
                  name: `${formData.firstName} ${formData.lastName}`,
                }
              )
            );
          },
        }
      ).then();
    },
    [bpContext, mutate, queryCache, intlMessage]
  );

  return (
    <>
      {isLoading && <Skeleton />}
      {!isLoading && data && (
        <BusinessPartnerPatientEditForm
          onSubmit={handleSubmit}
          initialValues={{
            ...data,
            generalSurveyRequired: data.generalSurveyRequired ?? false,
          }}
        >
          <SaveButton loading={mutationHelpers.isLoading} />
        </BusinessPartnerPatientEditForm>
      )}
    </>
  );
};
